
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Droplet, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

interface Reading {
  id: string;
  reading_value: number;
  reading_date: string;
  [key: string]: any;
}

interface WaterCalculatorProps {
  waterReadings: Reading[];
  waterReading1: string;
  setWaterReading1: (value: string) => void;
  waterReading2: string;
  setWaterReading2: (value: string) => void;
  waterResult: string;
  setWaterResult: (value: string) => void;
  selectedHouse?: string;
  selectedMonth?: string;
}

export const WaterCalculator = ({
  waterReadings,
  waterReading1,
  setWaterReading1,
  waterReading2,
  setWaterReading2,
  waterResult,
  setWaterResult = () => {},
  selectedHouse = "",
  selectedMonth = ""
}: WaterCalculatorProps) => {
  const [waterRate, setWaterRate] = useState<string>("");
  const [isCreatingExpense, setIsCreatingExpense] = useState(false);
  const { toast } = useToast();

  // State to store the parsed result values
  const [parsedCost, setParsedCost] = useState<number | null>(null);
  const [consumptionValue, setConsumptionValue] = useState<string>("");

  // Get selected reading data
  const selectedReading1 = waterReadings.find(r => r.id === waterReading1);
  const selectedReading2 = waterReadings.find(r => r.id === waterReading2);

  // Calculate result based on selected readings
  const calculateResult = () => {
    // Find the selected readings
    const reading1 = waterReadings.find(r => r.id === waterReading1);
    const reading2 = waterReadings.find(r => r.id === waterReading2);
    
    // Check if all required inputs are provided
    if (!reading1 || !reading2 || !waterRate || waterRate === '') {
      setWaterResult("Faltan valores");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    // Get reading values
    const value1 = parseFloat(reading1.reading_value as any);
    const value2 = parseFloat(reading2.reading_value as any);
    
    if (isNaN(value1) || isNaN(value2)) {
      setWaterResult("Error: Valores de lectura inválidos");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    // Get reading dates and convert to Date objects
    const date1 = new Date(reading1.reading_date);
    const date2 = new Date(reading2.reading_date);
    
    // Calculate the difference in days between dates
    const daysDiff = Math.abs((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));
    
    if (daysDiff === 0) {
      setWaterResult("Error: Las lecturas son del mismo día");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    // Calculate using the formula: (value2 - value1) / daysDiff * 30
    const consumptionPerMonth = (value2 - value1) / daysDiff * 30;
    
    // Format the results with $ amounts
    const waterRateValue = parseFloat(waterRate);
    if (isNaN(waterRateValue)) {
      setWaterResult("Error: El valor del m³ no es válido");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    const monthlyCost = consumptionPerMonth * waterRateValue;
    setParsedCost(monthlyCost);
    
    const formattedConsumption = new Intl.NumberFormat('es-CL', { 
      maximumFractionDigits: 2 
    }).format(consumptionPerMonth);
    
    setConsumptionValue(formattedConsumption);
    
    const formattedCost = new Intl.NumberFormat('es-CL', { 
      style: 'currency', 
      currency: 'CLP'
    }).format(monthlyCost);
    
    const result = `Consumo mensual: ${formattedConsumption} m³\nCosto estimado: ${formattedCost}`;
    setWaterResult(result);
  };

  // Run calculation when inputs change
  useEffect(() => {
    calculateResult();
  }, [waterReading1, waterReading2, waterRate]);

  const formatReadingDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Create an expense from the calculation
  const handleCreateExpense = async () => {
    if (!selectedHouse || !selectedMonth || !parsedCost || parsedCost <= 0) {
      toast({
        title: "Error",
        description: "Faltan datos para crear el gasto. Asegúrese de que haya seleccionado una casa y el cálculo sea válido.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreatingExpense(true);
      
      // Get the last day of the selected month for expense_date
      const [year, month] = selectedMonth.split("-").map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      const expenseDate = `${selectedMonth}-${lastDay.toString().padStart(2, '0')}`;

      // Fetch water category ID
      const { data: categories, error: categoryError } = await supabase
        .from("expense_categories")
        .select("id")
        .eq("name", "Agua")
        .limit(1);

      if (categoryError || !categories || categories.length === 0) {
        throw new Error("No se pudo encontrar la categoría Agua");
      }

      const categoryId = categories[0].id;
      
      // Insert the expense
      const { error } = await supabase.from("expenses").insert({
        entity_id: selectedHouse,
        entity_type: 'house',
        amount: Math.round(parsedCost),
        category: categoryId,
        description: `Consumo de agua: ${consumptionValue} m³`,
        expense_date: expenseDate,
      });

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Gasto de agua creado correctamente",
      });
    } catch (error) {
      console.error("Error creating expense:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el gasto de agua",
        variant: "destructive",
      });
    } finally {
      setIsCreatingExpense(false);
    }
  };

  // Check if the calculation result is valid for creating an expense
  const hasValidResult = 
    waterResult && 
    waterResult.includes("Costo estimado:") && 
    parsedCost !== null && 
    parsedCost > 0;

  return (
    <Card className="shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-medium">
          <Droplet className="h-5 w-5 text-blue-500" />
          Categoría Agua
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Primera Lectura</Label>
            <Select value={waterReading1} onValueChange={setWaterReading1}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleccione lectura" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {waterReadings.map((reading) => (
                  <SelectItem key={reading.id} value={reading.id}>
                    {`${reading.reading_value} - ${new Date(reading.reading_date).toLocaleDateString()}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReading1 && (
              <div className="text-sm text-gray-600 mt-1 ml-1">
                <p>Valor: {selectedReading1.reading_value} m³</p>
                <p>Fecha: {formatReadingDate(selectedReading1.reading_date)}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Segunda Lectura</Label>
            <Select value={waterReading2} onValueChange={setWaterReading2}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleccione lectura" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {waterReadings.map((reading) => (
                  <SelectItem key={reading.id} value={reading.id}>
                    {`${reading.reading_value} - ${new Date(reading.reading_date).toLocaleDateString()}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReading2 && (
              <div className="text-sm text-gray-600 mt-1 ml-1">
                <p>Valor: {selectedReading2.reading_value} m³</p>
                <p>Fecha: {formatReadingDate(selectedReading2.reading_date)}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="water-rate">Valor m³ del mes</Label>
            <Input
              id="water-rate"
              type="number"
              placeholder="Ingrese valor del m³"
              className="bg-white"
              value={waterRate}
              onChange={(e) => setWaterRate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="water-result">Resultado</Label>
            <div id="water-result" className="p-3 rounded-md border bg-gray-50 text-gray-700 whitespace-pre-line">
              {waterResult || "Resultado del cálculo"}
            </div>
          </div>

          <Button 
            onClick={handleCreateExpense} 
            disabled={!hasValidResult || !selectedHouse || isCreatingExpense}
            className="w-full"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Crear gasto de agua
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
